import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ui, SegmentedControl } from './ManagementUI';

export interface ManagementTool {
    id: string;
    label: string;
    description?: string;
    // Tools that don't act on the selected locations (e.g. the work queue) hide the location list
    usesLocations?: boolean;
    render: () => React.ReactNode;
}

interface ManagementWorkspaceProps {
    title: string;
    subtitle?: string;
    tools: ManagementTool[];
    activeToolId: string;
    onActiveToolChange: (toolId: string) => void;
    sidebar: React.ReactNode;
}

// Layout for the Management tab: a segmented control to pick a tool, the location list on the left
// and the selected tool on the right.
const ManagementWorkspace: React.FC<ManagementWorkspaceProps> = ({ title, subtitle, tools, activeToolId, onActiveToolChange, sidebar }) => {
    const activeTool = tools.find(tool => tool.id === activeToolId) ?? tools[0];
    const showSidebar = activeTool?.usesLocations !== false;

    // Tools stay mounted once opened, so switching away mid-update doesn't drop a running
    // bulk operation (or its progress) and let a second, conflicting one start
    const [openedToolIds, setOpenedToolIds] = useState<string[]>([]);
    if (activeTool && !openedToolIds.includes(activeTool.id)) {
        setOpenedToolIds([...openedToolIds, activeTool.id]);
    }

    return (
        <Workspace>
            <WorkspaceHeader>
                <WorkspaceTitle>{title}</WorkspaceTitle>
                {subtitle && <WorkspaceSubtitle>{subtitle}</WorkspaceSubtitle>}
            </WorkspaceHeader>

            <SegmentedControl
                options={tools.map(tool => ({ id: tool.id, label: tool.label }))}
                value={activeTool?.id}
                onChange={onActiveToolChange}
            />

            <Columns hasSidebar={showSidebar}>
                {showSidebar && <aside>{sidebar}</aside>}
                <ToolPane>
                    {activeTool?.description && <ToolDescription>{activeTool.description}</ToolDescription>}
                    {tools.filter(tool => tool.id === activeTool?.id || openedToolIds.includes(tool.id)).map(tool => (
                        <ToolView key={tool.id} hidden={tool.id !== activeTool?.id}>
                            {tool.render()}
                        </ToolView>
                    ))}
                </ToolPane>
            </Columns>
        </Workspace>
    );
};

const Workspace = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
    color: ${ui.label};
`;

const WorkspaceHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const WorkspaceTitle = styled.h2`
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${ui.label};
`;

const WorkspaceSubtitle = styled.p`
    margin: 0;
    font-size: 14px;
    color: ${ui.secondaryLabel};
`;

const Columns = styled.div<{ hasSidebar: boolean }>`
    display: grid;
    grid-template-columns: ${props => props.hasSidebar ? '320px minmax(0, 1fr)' : 'minmax(0, 1fr)'};
    gap: 28px;
    align-items: start;

    /* Give the location list more room on large monitors */
    @media (min-width: 1600px) {
        grid-template-columns: ${props => props.hasSidebar ? '380px minmax(0, 1fr)' : 'minmax(0, 1fr)'};
        gap: 32px;
    }

    @media (max-width: 1100px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const ToolPane = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
    min-width: 0;
`;

const ToolView = styled.div`
    display: flex;
    flex-direction: column;
    gap: 28px;
    min-width: 0;

    &[hidden] {
        display: none;
    }
`;

const ToolDescription = styled.p`
    margin: 0 0 -12px;
    font-size: 14px;
    line-height: 1.5;
    color: ${ui.secondaryLabel};
`;

export default ManagementWorkspace;
